import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice'
import AppPreset from './assets/styles/theme/primevue-preset'

import App from './App.vue'
import router from './router'
import { useAuthStore } from './stores/auth'
import { useSettingsStore } from './stores/settings'
import { initSentry, setSentryUser } from './utils/sentry'
import { initAnalytics, identifyUser } from './utils/analytics'
import { i18n, getCurrentLocale } from './plugins/i18n'

import './assets/styles/main.css'

const app = createApp(App)

// Initialize Sentry error tracking (must be done early)
initSentry(app, router, {
  dsn: import.meta.env.VITE_SENTRY_DSN ?? '',
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_APP_VERSION,
  tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),
})

// Initialize OpenPanel analytics
initAnalytics(router, {
  clientId: import.meta.env.VITE_OPENPANEL_CLIENT_ID ?? '',
  enabled: import.meta.env.VITE_ANALYTICS_ENABLED === 'true',
})

// Pinia state management
const pinia = createPinia()
app.use(pinia)

// Vue Router
app.use(router)

// Vue i18n for internationalization
app.use(i18n)

// Set initial document lang attribute
document.documentElement.setAttribute('lang', getCurrentLocale())

// PrimeVue with custom Material-inspired theme preset
// Note: darkModeSelector uses '.dark' to match Astro landing page
app.use(PrimeVue, {
  theme: {
    preset: AppPreset,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark',
    },
  },
})

// PrimeVue Toast service for notifications
app.use(ToastService)

// Initialize settings store from localStorage (before auth to prevent FOUC)
const settingsStore = useSettingsStore()
settingsStore.initializeFromStorage()

// Initialize auth store - triggers async token refresh if refresh cookie exists
// The router guards wait for initialization via waitForInitialization() before
// making auth decisions, so we can mount the app immediately
const authStore = useAuthStore()

// Start auth initialization (async) - sets up Sentry/analytics on success
authStore
  .initialize()
  .then(() => {
    // Set Sentry/analytics user context after successful auth initialization
    if (authStore.user) {
      setSentryUser({
        id: authStore.user.id,
        email: authStore.user.email,
      })
      identifyUser({
        id: authStore.user.id,
        email: authStore.user.email,
      })
    }
  })
  .catch(() => {
    // Initialization completed without authentication - this is expected for unauthenticated users
  })

// Mount app immediately - router guards handle waiting for auth state
app.mount('#app')
